import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const calendarService = {
  async getCompanyId() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return data?.company_id || null;
    } catch {
      return null;
    }
  },

  async createEvent(eventData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const companyId = await this.getCompanyId();

      const snakeData = toSnakeCase({
        ...eventData,
        companyId,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create calendar event:', error);
      throw error;
    }
  },

  async updateEvent(eventId, updates) {
    try {
      const snakeData = toSnakeCase({ ...updates, updatedAt: new Date()?.toISOString() });
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.update(snakeData)
        ?.eq('id', eventId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update calendar event:', error);
      throw error;
    }
  },

  async deleteEvent(eventId) {
    try {
      const { error } = await supabase
        ?.from('calendar_events')
        ?.delete()
        ?.eq('id', eventId);

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      console.error('Failed to delete calendar event:', error);
      throw error;
    }
  },

  async getEvents(startDate, endDate) {
    try {
      const companyId = await this.getCompanyId();

      let query = supabase
        ?.from('calendar_events')
        ?.select(`
          *,
          client:clients(id, nombre, telefono),
          vehicle:vehicles(id, marca, modelo, year, placas),
          assigned_user:user_profiles!calendar_events_assigned_to_fkey(full_name)
        `)
        ?.order('start_datetime', { ascending: true });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      if (startDate) {
        query = query?.gte('start_datetime', startDate);
      }

      if (endDate) {
        query = query?.lte('start_datetime', endDate);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      return [];
    }
  },

  // Auto-create event when work order is created
  async createWorkOrderEvent(workOrder) {
    try {
      return await this.createEvent({
        title: `OT: ${workOrder?.folioOt || workOrder?.folio_ot}`,
        description: workOrder?.motivoIngreso || workOrder?.motivo_ingreso || 'Nueva orden de trabajo',
        relatedType: 'work_order',
        relatedId: workOrder?.id,
        clientId: workOrder?.clientId || workOrder?.client_id,
        vehicleId: workOrder?.vehicleId || workOrder?.vehicle_id,
        startDatetime: workOrder?.fechaIngreso || workOrder?.fecha_ingreso || new Date()?.toISOString(),
        endDatetime: workOrder?.fechaEstimadaEntrega || workOrder?.fecha_estimada_entrega,
        status: 'scheduled',
        color: '#3B82F6'
      });
    } catch (error) {
      console.error('Failed to create work order calendar event:', error);
      return null;
    }
  }
};
